/**
 * Recurring tasks (18 Oct 2026 — web first; Chad's sequence: Insights →
 * recurring → calendar export). Pure: no store, no clock unless passed.
 *
 * A repeating task is ONE task on the board at a time. Ticking it done
 * creates the next copy (`nextInstance`), dated by `nextOccurrence`:
 *   - counted on from the task's OWN date, not the day it was finished, so
 *     a weekly Monday task stays a Monday task however late it is done;
 *   - always strictly after today, so finishing never drops a fresh copy
 *     straight back into Today;
 *   - a task with no date starts from today.
 * The copy keeps zone, tags, scores and plan steps (unticked) — it re-enters
 * through the zone the AI gave it — and drops the note, hand-off, hand-order
 * and rollover count. Its id is derived from the series and the date
 * (`seriesId~YYYY-MM-DD`), so two devices completing the same task create
 * the SAME copy and sync merges them instead of doubling it.
 */
import type { Task } from '../types';
import { formatDateHint, formatTime } from './dates';
import { localDateKey } from './affirmation';

export type RepeatRule = NonNullable<Task['repeat']>;

export const REPEAT_OPTIONS: { id: RepeatRule; label: string; badge: string }[] = [
  { id: 'day', label: 'Every day', badge: 'Daily' },
  { id: 'weekday', label: 'Every weekday', badge: 'Weekdays' },
  { id: 'week', label: 'Every week', badge: 'Weekly' },
  { id: 'month', label: 'Every month', badge: 'Monthly' },
];

export function isRepeatRule(v: unknown): v is RepeatRule {
  return v === 'day' || v === 'weekday' || v === 'week' || v === 'month';
}

export function repeatBadge(rule: RepeatRule | undefined): string | undefined {
  return REPEAT_OPTIONS.find((o) => o.id === rule)?.badge;
}

function step(d: Date, rule: RepeatRule): Date {
  const n = new Date(d.getTime());
  if (rule === 'day') n.setDate(n.getDate() + 1);
  else if (rule === 'week') n.setDate(n.getDate() + 7);
  else if (rule === 'weekday') {
    do n.setDate(n.getDate() + 1);
    while (n.getDay() === 0 || n.getDay() === 6);
  } else {
    // Same day next month, clamped to the month's last day (31 Jan → 28 Feb).
    const day = n.getDate();
    n.setDate(1);
    n.setMonth(n.getMonth() + 1);
    const last = new Date(n.getFullYear(), n.getMonth() + 1, 0).getDate();
    n.setDate(Math.min(day, last));
  }
  return n;
}

/**
 * The next date for this rule after `fromAt` that is strictly after
 * `now`'s local day. Keeps the time of day of `fromAt` (noon for
 * date-only tasks).
 */
export function nextOccurrence(rule: RepeatRule, fromAt: number | undefined, now: number): number {
  const today = localDateKey(new Date(now));
  let d: Date;
  if (fromAt === undefined || !Number.isFinite(fromAt)) {
    const n = new Date(now);
    d = new Date(n.getFullYear(), n.getMonth(), n.getDate(), 12, 0, 0, 0);
  } else {
    d = new Date(fromAt);
  }
  // At most ~3 years of daily steps; a guard, never reached in practice.
  for (let i = 0; i < 1200; i += 1) {
    d = step(d, rule);
    if (localDateKey(d) > today) return d.getTime();
  }
  return d.getTime();
}

/** The display text for a timestamp, matching the date picker's format. */
export function hintFor(at: number): string {
  const d = new Date(at);
  const noon = d.getHours() === 12 && d.getMinutes() === 0;
  return noon ? formatDateHint(d) : `${formatDateHint(d)}, ${formatTime(d.getHours(), d.getMinutes())}`;
}

export function seriesId(taskId: string): string {
  return taskId.split('~')[0];
}

/** The next copy of a repeating task, or undefined when it does not repeat. */
export function nextInstance(task: Task, now: number, newStepId: () => string): Task | undefined {
  if (!isRepeatRule(task.repeat)) return undefined;
  const at = nextOccurrence(task.repeat, task.deadlineAt, now);
  const next: Task = {
    id: `${seriesId(task.id)}~${localDateKey(new Date(at))}`,
    text: task.text,
    zone: task.zone,
    urgency: task.urgency,
    importance: task.importance,
    deadlineHint: hintFor(at),
    deadlineAt: at,
    steps: task.steps.map((s) => ({ id: newStepId(), text: s.text, minutes: s.minutes, done: false })),
    stepsGenerated: task.stepsGenerated,
    done: false,
    rolloverCount: 0,
    repeat: task.repeat,
    createdAt: now,
    editedAt: now,
  };
  if (task.tags && task.tags.length) next.tags = [...task.tags];
  return next;
}

/** Undo guard: the copy may be removed only while nobody has touched it. */
export function isUntouchedInstance(t: Task): boolean {
  return !t.done && t.editedAt === t.createdAt && !t.startedAt && t.steps.every((s) => !s.done && !s.startedAt);
}
